import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useTokenUsageSummary } from '@/hooks/useTokenUsage'
import type { AgentTokenSummary } from '@/lib/api'

function formatCost(usd: number): string {
  if (usd < 0.01) return '<$0.01'
  return `$${usd.toFixed(2)}`
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function TopAgents({ agents, field }: { agents: AgentTokenSummary[]; field: 'cost' | 'failures' }) {
  const sorted = [...agents]
  if (field === 'cost') {
    sorted.sort((a, b) => b.cost_7d_usd - a.cost_7d_usd)
  } else {
    sorted.sort((a, b) => (100 - a.success_rate) - (100 - b.success_rate))
    // Only show agents with failures
    const filtered = sorted.filter((a) => a.success_rate < 100)
    sorted.length = 0
    sorted.push(...filtered)
  }

  const top3 = sorted.slice(0, 3)
  if (top3.length === 0) return <p className="text-sm text-cmd-outline font-label px-1">NONE</p>

  return (
    <div className="space-y-1.5">
      {top3.map((agent) => (
        <div key={agent.name} className="flex items-center justify-between text-sm">
          <span className="text-white/80 font-mono text-xs uppercase truncate">
            {agent.name}
          </span>
          {field === 'cost' ? (
            <span className="font-mono text-xs text-cmd-outline tabular-nums">
              {formatCost(agent.cost_7d_usd)}
            </span>
          ) : (
            <Badge variant={agent.success_rate < 50 ? 'destructive' : 'default'}>
              {agent.success_rate}%
            </Badge>
          )}
        </div>
      ))}
    </div>
  )
}

export function TokenUsageWidget() {
  const { data, isLoading } = useTokenUsageSummary()

  return (
    <Card>
      <CardHeader>
        <CardTitle>TOKEN USAGE</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : data ? (
          <>
            {/* Cost summary */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="font-label text-[0.65rem] text-cmd-outline uppercase">Today</p>
                <p className="font-mono text-lg font-medium text-white tabular-nums">
                  {formatCost(data.total_cost_24h_usd)}
                </p>
              </div>
              <div>
                <p className="font-label text-[0.65rem] text-cmd-outline uppercase">This Week</p>
                <p className="font-mono text-lg font-medium text-white tabular-nums">
                  {formatCost(data.total_cost_7d_usd)}
                </p>
              </div>
            </div>

            {/* Top consumers */}
            <div>
              <p className="font-label text-[0.65rem] text-cmd-outline uppercase mb-2">
                Top Consumers (7d)
              </p>
              <TopAgents agents={data.agents} field="cost" />
            </div>

            {/* Failing agents */}
            <div>
              <p className="font-label text-[0.65rem] text-cmd-outline uppercase mb-2">
                Failing Agents
              </p>
              <TopAgents agents={data.agents} field="failures" />
            </div>

            {/* Bar chart: tokens per agent */}
            <div>
              <p className="font-label text-[0.65rem] text-cmd-outline uppercase mb-2">
                Tokens by Agent (7d)
              </p>
              <AgentTokenBars agents={data.agents} />
            </div>
          </>
        ) : (
          <p className="text-sm text-cmd-outline font-label">NO DATA</p>
        )}
      </CardContent>
    </Card>
  )
}

function AgentTokenBars({ agents }: { agents: AgentTokenSummary[] }) {
  const sorted = [...agents].sort((a, b) => b.tokens_7d - a.tokens_7d).slice(0, 8)
  const max = sorted[0]?.tokens_7d || 1

  return (
    <div className="space-y-1.5">
      {sorted.map((agent) => {
        const successPct = agent.tokens_7d > 0
          ? (agent.tokens_7d * (agent.success_rate / 100)) / max * 100
          : 0
        const failPct = agent.tokens_7d > 0
          ? (agent.tokens_7d * ((100 - agent.success_rate) / 100)) / max * 100
          : 0

        return (
          <div key={agent.name} className="flex items-center gap-2">
            <span className="w-20 text-right font-mono text-[0.6rem] text-cmd-outline uppercase truncate shrink-0">
              {agent.name}
            </span>
            <div className="flex-1 flex h-3 bg-cmd-low rounded-sm overflow-hidden">
              <div
                className="bg-primary h-full transition-all"
                style={{ width: `${successPct}%` }}
              />
              <div
                className="bg-cmd-error h-full transition-all"
                style={{ width: `${failPct}%` }}
              />
            </div>
            <span className="w-12 text-right font-mono text-[0.6rem] text-cmd-outline tabular-nums shrink-0">
              {formatTokens(agent.tokens_7d)}
            </span>
          </div>
        )
      })}
    </div>
  )
}
